/**
 * Filename sanitization for files written into the sandbox workspace.
 *
 * Upload names come straight from the user's disk (or a paste), so they can contain
 * path separators, `..`, control characters, or names long enough to break a filesystem.
 * Everything that reaches `resourceWrite` goes through here first. The host confines
 * writes to its workspace roots regardless, but a name that escapes the intended upload
 * directory would still scatter files across the workspace, so this is the first defence
 * rather than the only one.
 */

/**
 * Longest basename we emit, excluding the extension. Comfortably under the 255-byte
 * limit common to ext4/APFS even after the collision suffix and percent-encoding.
 */
const MAX_BASE_LENGTH = 120

/** Longest extension we preserve; anything longer is treated as part of the name. */
const UNSAFE_FALLBACK_NAME = 'upload'

const MAX_EXTENSION_LENGTH = 16

const UNSAFE_CHARACTERS = /[^A-Za-z0-9._-]+/g
const LEADING_DOTS_OR_SEPARATORS = /^[.\-_]+/
const TRAILING_DOTS_OR_SEPARATORS = /[.\-_]+$/

function splitExtension(name: string): {base: string; extension: string} {
  const dot = name.lastIndexOf('.')
  // `dot <= 0` covers both "no extension" and a dotfile like `.env`, where the leading
  // dot is part of the name rather than an extension.
  if (dot <= 0 || dot === name.length - 1) return {base: name, extension: ''}
  const extension = name.slice(dot)
  if (extension.length > MAX_EXTENSION_LENGTH) return {base: name, extension: ''}
  return {base: name.slice(0, dot), extension}
}

/**
 * Reduces an arbitrary upload name to a single safe path segment.
 *
 * Always returns a non-empty segment: a name that sanitizes away entirely (`"../.."`,
 * `"😀"`, `""`) falls back to `fallback`, which callers seed with a unique id so two
 * such files don't collide with each other.
 *
 * The fallback is sanitized by the same rules rather than trusted, so this function
 * cannot emit a traversal segment even if a caller seeds it with attacker-controlled
 * input. A fallback that is itself unsafe degrades to `UNSAFE_FALLBACK_NAME`.
 */
function sanitizeSegment(value: string): string {
  return (value.split(/[/\\]/).pop() ?? '')
    .normalize('NFC')
    .replace(UNSAFE_CHARACTERS, '_')
    .replace(/_{2,}/g, '_')
    .replace(LEADING_DOTS_OR_SEPARATORS, '')
    .replace(TRAILING_DOTS_OR_SEPARATORS, '')
    .slice(0, MAX_BASE_LENGTH)
}

export function sanitizeUploadFilename(rawName: string, fallback: string): string {
  // Take the last segment on either separator, so `../../etc/passwd` and a Windows
  // `C:\tmp\x.txt` both reduce to their basename before any other rule runs.
  const basename = rawName.split(/[/\\]/).pop() ?? ''
  // NFC first: a decomposed name can otherwise pass the character filter and then be
  // normalized differently by the filesystem, so the path we report wouldn't be the
  // path on disk.
  const normalized = basename.normalize('NFC')

  const {base, extension} = splitExtension(normalized)

  const safeBase = base
    .replace(UNSAFE_CHARACTERS, '_')
    .replace(/_{2,}/g, '_')
    .replace(LEADING_DOTS_OR_SEPARATORS, '')
    .replace(TRAILING_DOTS_OR_SEPARATORS, '')
    .slice(0, MAX_BASE_LENGTH)

  const safeExtension = extension.replace(UNSAFE_CHARACTERS, '_').toLowerCase()

  if (safeBase.length === 0) {
    const safeFallback = sanitizeSegment(fallback) || UNSAFE_FALLBACK_NAME
    return safeExtension.length > 0 ? `${safeFallback}${safeExtension}` : safeFallback
  }

  return `${safeBase}${safeExtension}`
}

/**
 * Nth candidate name for a file whose earlier candidates already existed on disk.
 *
 * `attempt` 0 is the original name; later attempts insert `-1`, `-2`, … before the
 * extension. We never overwrite, because the agent may already be reading the file
 * that is in the way.
 */
export function withCollisionSuffix(name: string, attempt: number): string {
  if (attempt === 0) return name
  const {base, extension} = splitExtension(name)
  return `${base.slice(0, MAX_BASE_LENGTH)}-${attempt}${extension}`
}
